import { WorkflowExecutionId, WorkflowTargetId } from "@sp-crm/core";
import { InputFormBlock } from "components/app/input-form-block";
import { QueryRenderer } from "components/clients/show-client/community-comparison/query-renderer";
import { Header } from "components/header";
import {
    WorkflowActionChip,
    workflowActionChips,
} from "components/manage/manage-workflows/builder/builder-helper";
import { ActionChip } from "components/manage/manage-workflows/builder/sequence-strip";
import { DeleteButton } from "components/ui/action-button";
import {
    GetWorkflowExecutionsForEntityQuery,
    useCancelWorkflowExecutionMutation,
    useGetWorkflowExecutionsForEntityQuery,
} from "generated/graphql";
import React, { useCallback, useEffect, useMemo } from "react";
import { WorkflowRunSequenceCard } from "./workflow-run-sequence";
import { groupWorkflowRunSequences, WorkflowRunSequence } from "./workflow-run-sequences";
import {
    ResultDetail,
    runDisplayState,
    RunStatusIcon,
    runStatusText,
    runStatusTextClassName,
    runTitleClassName,
    stopWorkflowConfirm,
    WorkflowRunState,
} from "./workflow-run-status";

interface WorkflowEntity {
    id: WorkflowTargetId;
}

interface WorkflowEntityExecutionsProps {
    entity: WorkflowEntity;
}

type WorkflowEntityExecution =
    GetWorkflowExecutionsForEntityQuery["getWorkflowExecutionsForEntity"][0];

type EntityRunSequence = WorkflowRunSequence<WorkflowEntityExecution>;

type CancelRun = (executionId: WorkflowExecutionId) => Promise<void>;

const isMultiAction = (sequence: EntityRunSequence): boolean =>
    sequence.workflow.actions.length > 1;

export const WorkflowEntityExecutions: React.FC<
    WorkflowEntityExecutionsProps
> = props => {
    const { entity } = props;
    const workflowExecutionsQuery = useGetWorkflowExecutionsForEntityQuery({
        workflowTargetId: entity.id,
    });
    const cancelMutation = useCancelWorkflowExecutionMutation();
    const refetch = workflowExecutionsQuery.refetch;
    const cancel = useCallback<CancelRun>(
        async executionId => {
            await cancelMutation.mutateAsync({ workflowExecutionId: executionId });
            refetch();
        },
        [cancelMutation, refetch],
    );

    useEffect(() => {
        workflowExecutionsQuery.refetch();
    }, [entity]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <QueryRenderer name="WorkflowEntityExecutions" query={workflowExecutionsQuery}>
            {data => (
                <WorkflowRuns
                    executions={data.getWorkflowExecutionsForEntity}
                    onCancel={cancel}
                />
            )}
        </QueryRenderer>
    );
};

interface WorkflowRunsProps {
    executions: WorkflowEntityExecution[];
    onCancel: CancelRun;
}

const WorkflowRuns: React.FC<WorkflowRunsProps> = props => {
    const { executions, onCancel } = props;
    const sequences = useMemo(() => groupWorkflowRunSequences(executions), [executions]);
    const openSequenceId = sequences.find(isMultiAction)?.id ?? null;

    if (sequences.length === 0) {
        return null;
    }
    return (
        <InputFormBlock>
            <Header iconName="36_CheckBackLater">Workflow automation</Header>
            <div className="space-y-2 mt-2">
                {sequences.map(sequence =>
                    isMultiAction(sequence) ? (
                        <WorkflowRunSequenceCard
                            key={sequence.id}
                            sequence={sequence}
                            disclosure={
                                sequence.id === openSequenceId ? "open" : "collapsed"
                            }
                            onCancel={execution => onCancel(execution.id)}
                        />
                    ) : (
                        <SingleActionRuns
                            key={sequence.id}
                            sequence={sequence}
                            onCancel={onCancel}
                        />
                    ),
                )}
            </div>
        </InputFormBlock>
    );
};

interface SingleActionRunsProps {
    sequence: EntityRunSequence;
    onCancel: CancelRun;
}

const SingleActionRuns: React.FC<SingleActionRunsProps> = props => {
    const { sequence, onCancel } = props;
    const chip = workflowActionChips(sequence.workflow)[0] ?? null;
    return (
        <>
            {sequence.rows.map(row =>
                row.kind === "executed" ? (
                    <div
                        key={row.execution.id}
                        className="rounded-md border border-gray-200 px-3 py-2">
                        <WorkflowRunRow
                            title={sequence.workflow.name}
                            chip={chip}
                            run={row.execution}
                            onCancel={() => onCancel(row.execution.id)}
                        />
                    </div>
                ) : null,
            )}
        </>
    );
};

interface WorkflowRunRowProps {
    title: React.ReactNode;
    chip: WorkflowActionChip | null;
    run: WorkflowRunState;
    onCancel: () => Promise<void>;
}

export const WorkflowRunRow: React.FC<WorkflowRunRowProps> = props => {
    const { title, chip, run, onCancel } = props;
    const state = runDisplayState(run);
    const statusText = runStatusText(run);
    return (
        <div>
            <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-1">
                <div className="flex flex-wrap items-center gap-2">
                    <RunStatusIcon state={state} />
                    <div className={runTitleClassName(state)}>{title}</div>
                    {chip ? <ActionChip chip={chip} /> : null}
                </div>
                {state === "scheduled" ? (
                    <div className="flex items-center space-x-2 pl-7 sm:pl-0">
                        <div className={runStatusTextClassName}>{statusText}</div>
                        <DeleteButton
                            confirm={stopWorkflowConfirm}
                            onClick={onCancel}
                            backgroundColor="bg-gray-200"
                        />
                    </div>
                ) : statusText ? (
                    <div className="flex items-center pl-7 pr-9 sm:pl-0">
                        <div className={runStatusTextClassName}>{statusText}</div>
                    </div>
                ) : null}
            </div>
            <ResultDetail detail={run.resultDetail} className="pl-7 pr-9" />
        </div>
    );
};
